import { Badge, Flex, Menu, MenuButton, MenuItem, MenuList, Text, useTheme, useToast } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { issbKeys } from "hooks/query";
import { showErrorToast } from "utils";
import { useQueryClient } from "react-query";
import assert from "utils/assert";
import Issb from "api/issb/issb";
import { toTitleCase } from "utils/text-utils";
import { Spinner } from "@phosphor-icons/react";
import LocalSpinner from "../../../../components/LocalSpinner";
import useAirUserRoles from "../../../../utils/AuthUtils";
import StatusToggle from "./StatusToggle";

/** Note: If unset, use the value as the label */
export const STATUSFIELD_VALUE_LABEL = Object.freeze({
  // Grammar note: "completeness" not "complete", because "complete:low" would be ungrammatical, and also "complete" is used in ESG360 questions in a different way (as a workflow-stage).
  // ** Do not remove the child values even it's not needed for labels, because it's used in Dashbaord to look for possible values of field
  completeness: {
    NONE: "No answer",
    LOW: "Requires further data",
    MEDIUM: "Requires iteration",
    HIGH: "Done", // Note: "Done" to avoid giving "complete" conflicting meanings in ESG360 questions.
  },
  decisionUsefulness: {
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
  }, // label as-is
  sensitivity: {
    LOW: "Low",
    MEDIUM: "Medium",
    HIGH: "High",
  }, // The spec did ask for "Green","Amber","Red" -- but unless the color used also changes to match, then that would be confusing.
  // Note: having e.g. word:"green",color:red is actually textbook confusing -- it is used in cognitive science tests as a way to confuse and hence reset the test-subject's concentration.
  // Good setups are either: all-labels-change-colour, or no-colour-changes no-colour words
  confidence: {
    NONE: "None",
    LOW: "Low",
    MEDIUM: "Limited",
    HIGH: "Reasonable",
  },
});

/**
 * @param questionIds {array}
 * @param statuses {[field]: value} e.g. {completeness: "NONE"}
 */
const AdditionalStatuses = ({ questionIds, statuses, readOnly }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { colors } = useTheme();

  const { isAssigned } = useAirUserRoles();

  const isAssigneeOfQuestions = () => {
    return questionIds?.every((qId) => isAssigned(qId));
  };

  const handleChange = (field, status) => {
    assert(field && status);
    const dataToSubmit = {
      questionIds: questionIds,
      field,
      status,
    };
    return Issb.changeStatus(dataToSubmit)
      .then(() => {
        // triggers loss of other edits to a question (see Jira ESG-2678)
        // But that shouldn't be an issue if its being applied to a list via bulk-actions
        if (questionIds.length > 1) {
          queryClient.invalidateQueries(issbKeys.answer());
        }
      })
      .catch((err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message));
  };

  return (
    <Flex direction="column" gap={3}>
      <Flex gap={2} align="center">
        <Spinner color={colors.gray[400]} size={18} />
        <Text variant="statusTitle">Status</Text>
      </Flex>
      <AdditionalStatusField
        field="completeness"
        status={statuses?.completeness}
        handleChange={handleChange}
        colorScheme="orange"
        changeDisabled={!isAssigneeOfQuestions() || readOnly}
        readOnly={readOnly}
      />
      <AdditionalStatusField
        field="decisionUsefulness"
        label="Decision Usefulness"
        status={statuses?.decisionUsefulness}
        handleChange={handleChange}
        colorScheme="green"
        changeDisabled={!isAssigneeOfQuestions() || readOnly}
        readOnly={readOnly}
      />
      <AdditionalStatusField
        field="sensitivity"
        status={statuses?.sensitivity}
        handleChange={handleChange}
        colorScheme="red"
        changeDisabled={!isAssigneeOfQuestions() || readOnly}
        readOnly={readOnly}
      />
      <AdditionalStatusField
        field="confidence"
        status={statuses?.confidence}
        handleChange={handleChange}
        colorScheme="green"
        changeDisabled={!isAssigneeOfQuestions() || readOnly}
      />
    </Flex>
  );
};

function AdditionalStatusField({ field, status, label, handleChange, colorScheme, changeDisabled }) {
  const labeller = (status) => {
    return STATUSFIELD_VALUE_LABEL[field][status] ?? toTitleCase(status);
  };
  const values = "HIGH MEDIUM LOW".split(" ");
  // HACK allow NONE for completeness (meaning no-answer)
  if (field === "completeness" || field === "confidence") values.push("NONE");
  return (
    <StatusToggle
      field={field}
      status={status}
      values={values}
      label={label}
      valueLabeller={labeller}
      handleChange={handleChange}
      colorScheme={colorScheme}
      changeDisabled={changeDisabled}
    />
  );
}

export default AdditionalStatuses;
